// NOTE: Client-side CSR validation is a UX optimization only, not a security boundary.
// The backend performs full cryptographic validation including signature verification.

// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0

/**
 * Client-side CSR validation — UX optimization only, NOT a security boundary.
 *
 * This validator performs lightweight structural checks (subject CN, serial
 * extension) to give the user immediate feedback before submitting the CSR
 * to the backend. It does NOT verify the CSR's cryptographic signature.
 *
 * The backend {@link CsrValidator} (packages/backend/src/csr-validator.ts)
 * performs full cryptographic validation including:
 *   - PKCS#10 self-signature verification (proof of possession)
 *   - Key type / size enforcement
 *   - Subject CN and serial extension matching
 *
 * Any CSR that passes client-side validation may still be rejected by the
 * backend. Never rely on this module as a security control.
 */

import { CertificationRequest, Extensions, type Extension } from "pkijs";
import * as asn1js from "asn1js";

/** Custom OID for YubiKey serial number extension */
const YUBIKEY_SERIAL_OID = "1.3.6.1.4.1.99999.1.1";

/** OID for the extensionRequest attribute (PKCS#9) */
const EXTENSION_REQUEST_OID = "1.2.840.113549.1.9.14";

/** OID for Common Name */
const CN_OID = "2.5.4.3";

export interface CsrValidationResult {
  subjectCN: string;
  extensionSerial: string;
}

export interface IWebCsrValidator {
  validateCsr(
    csrPem: string,
    expectedCN: string,
    expectedSerial: string
  ): CsrValidationResult;
}

/**
 * Decodes a PEM-encoded CSR into raw DER bytes.
 */
function pemToDer(pem: string): ArrayBuffer {
  const lines = pem
    .replace(/-----BEGIN CERTIFICATE REQUEST-----/, "")
    .replace(/-----END CERTIFICATE REQUEST-----/, "")
    .replace(/\s+/g, "");
  const binary = atob(lines);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Extracts the subject CN from a CertificationRequest.
 */
function extractSubjectCN(csr: CertificationRequest): string | undefined {
  for (const rdn of csr.subject.typesAndValues) {
    if (rdn.type === CN_OID) {
      return rdn.value.valueBlock.value;
    }
  }
  return undefined;
}

/**
 * Extracts the YubiKey serial from the custom extension (OID 1.3.6.1.4.1.99999.1.1)
 * embedded in the CSR's extensionRequest attribute.
 */
function extractSerialExtension(
  csr: CertificationRequest
): string | undefined {
  if (!csr.attributes) return undefined;

  for (const attr of csr.attributes) {
    if (attr.type === EXTENSION_REQUEST_OID && attr.values.length > 0) {
      const extensions = new Extensions({ schema: attr.values[0] });
      for (const ext of extensions.extensions) {
        if (ext.extnID === YUBIKEY_SERIAL_OID) {
          return decodeSerialValue(ext);
        }
      }
    }
  }
  return undefined;
}

/**
 * Decodes the serial value from the extension's extnValue.
 * The value is expected to be a DER-encoded UTF8String.
 */
function decodeSerialValue(ext: Extension): string {
  const asn = asn1js.fromBER(ext.extnValue.valueBlock.valueHexView);
  if (asn.offset === -1) {
    throw new Error("Failed to decode serial extension value");
  }
  const result = asn.result;
  // If it's a UTF8String or PrintableString, extract the value
  if ("value" in result.valueBlock) {
    return (result.valueBlock as { value: string }).value;
  }
  // Fallback: decode raw bytes as UTF-8
  const valueBlock = result.valueBlock as { valueHexView?: Uint8Array };
  if (valueBlock.valueHexView) {
    return new TextDecoder().decode(valueBlock.valueHexView);
  }
  throw new Error("Unable to decode serial extension value");
}

export class WebCsrValidator implements IWebCsrValidator {
  validateCsr(
    csrPem: string,
    expectedCN: string,
    expectedSerial: string
  ): CsrValidationResult {
    // Parse the PEM-encoded CSR
    let csr: CertificationRequest;
    try {
      const der = pemToDer(csrPem);
      csr = CertificationRequest.fromBER(der);
    } catch {
      throw new Error("Failed to parse CSR: the certificate request is malformed.");
    }

    // Extract and verify subject CN
    const subjectCN = extractSubjectCN(csr);
    if (!subjectCN) {
      throw new Error(
        "CSR subject mismatch: the certificate request does not contain a Common Name (CN)."
      );
    }
    if (subjectCN !== expectedCN) {
      throw new Error(
        `CSR subject mismatch: expected CN "${expectedCN}" but got "${subjectCN}".`
      );
    }

    // Extract and verify serial from custom extension
    const extensionSerial = extractSerialExtension(csr);
    if (!extensionSerial) {
      throw new Error(
        "CSR serial mismatch: the certificate request does not contain the YubiKey serial extension."
      );
    }
    if (extensionSerial !== expectedSerial) {
      throw new Error(
        `CSR serial mismatch: expected serial "${expectedSerial}" but got "${extensionSerial}".`
      );
    }

    return { subjectCN, extensionSerial };
  }
}
